import {getAuth, signInWithCustomToken} from 'firebase/auth';
import queryString from 'query-string';
import React from 'react';
import {useQueryClient} from 'react-query';
import {Navigate, useLocation, useNavigate} from 'react-router-dom';
import {StringParam, useQueryParam} from 'use-query-params';
import {useStateWithDeps} from 'use-state-with-deps';

import useAuth, {
  Auth,
  AuthContext,
  LoggedInAuth,
  LoggedOutAuth,
  getOrgWithIdPrefix,
  makeLoggedInAuth,
  makeLoggedOutAuth,
} from 'src/hooks/useAuth';
import {useCurrentOrgIdPrefix} from 'src/hooks/useCurrentOrgIdPrefix';
import useFirebaseIdToken from 'src/hooks/useFirebaseIdToken';
import useFirebaseUser from 'src/hooks/useFirebaseUser';
import useOrganizationQuery from 'src/hooks/useOrganization';
import useUserProfileQuery from 'src/hooks/useUserProfile';
import {
  NETWORK_RESTRICTED_EVENT,
  NetworkRestrictedDetail,
  setContextOrganizationId,
} from 'src/lib/api/api';
import {ApiOrganization, ApiUserProfile} from 'src/lib/api/auth';
import {
  LOGIN_APP_URL,
  redirectToLoginApp,
  setWindowLocationHref,
  startSignOutHandoff,
} from 'src/lib/routeUtils';

import NetworkRestricted from './NetworkRestricted';
import {useTranslations} from './TranslationsContext';

const POST_SIGNIN_PATH_KEY = 'n';

export const WithAuth: React.FunctionComponent<{
  children: (auth: Auth) => React.ReactNode;
}> = ({children}) => {
  const auth = useAuth();
  return children(auth);
};

/**
 * Renders its child function prop if auth is logged out, otherwise redirects to
 * redirectPath.
 */
export const WithLoggedOutAuth: React.FunctionComponent<{
  children: (auth: LoggedOutAuth) => React.ReactNode;
}> = ({children}) => {
  const auth = useAuth();
  const {search} = useLocation();

  if (auth.status === 'logged out') {
    return children(auth);
  } else {
    const redirectPath = queryString.parse(search)[POST_SIGNIN_PATH_KEY] || '/projects';
    return <Navigate to={redirectPath as string} replace />;
  }
};

/**
 * Renders its child function prop if auth is logged in, otherwise redirects to
 * redirectPath.
 *
 * Ensures that the user has both a profile and an organization, which are
 * passed as additional args to the children function.
 *
 * If you want to handle cases where the user is signed in but does not have a
 * profile or organization (perhaps they were removed from them, but they still
 * have a Firebase account) use WithAuth.
 */
export const WithLoggedInAuth: React.FunctionComponent<{
  children: (
    auth: LoggedInAuth,
    currentUser: ApiUserProfile,
    organizations: {
      currentOrganization: ApiOrganization;
      profileOrganizations: ApiOrganization[];
    }
  ) => React.ReactNode;
}> = ({children}) => {
  const auth = useAuth();
  const {t} = useTranslations();
  const {pathname, hash} = useLocation();
  const navigate = useNavigate();
  const currentOrgIdPrefix = useCurrentOrgIdPrefix(false);

  React.useEffect(() => {
    if (auth.status === 'logged in') {
      const {profileOrganizations} = auth;
      // This check allows us to set the currentOrganization based on the Organization ID prefix slug in a URL.
      // The CurrentOrgIdPrefix context sets this from a route. This is needed to build URLs from a child org
      // that a user in the parent org can open (since otherwise we won't know which child org the URL is for).
      if (currentOrgIdPrefix) {
        const orgWithIdPrefix = getOrgWithIdPrefix(currentOrgIdPrefix, profileOrganizations);
        // Case: This slug matches an org that the user has access to. Set that org as the current org in state and context.
        if (orgWithIdPrefix) {
          auth.setCurrentOrganization(orgWithIdPrefix);
          setContextOrganizationId(orgWithIdPrefix.id);
          return;
        }

        // Case: This slug does not match any org the user has access to (all profile orgs and their children). Fall back to first profile org.
        if (profileOrganizations[0]) {
          auth.setCurrentOrganization(profileOrganizations[0]);
          setContextOrganizationId(profileOrganizations[0].id);
        }
      }
    }
  }, [auth, currentOrgIdPrefix]);

  if (auth.status === 'logged in') {
    const {userProfile: currentUser, profileOrganizations, currentOrganization} = auth;

    // A link into an org that refuses this network finds no org to switch to —
    // useOrganizationQuery left it out — so without this it would quietly open another of the
    // user's orgs instead.
    const isLinkToRestrictedOrganization =
      !!currentOrgIdPrefix &&
      !getOrgWithIdPrefix(currentOrgIdPrefix, profileOrganizations) &&
      !!currentUser?.organizations.some(
        (org) => org.networkRestricted && org.id.startsWith(currentOrgIdPrefix)
      );
    if (isLinkToRestrictedOrganization) {
      return (
        <NetworkRestricted
          onSwitchOrganization={() => navigate('/organizations')}
          onSignOut={auth.signOut}
        />
      );
    }

    if (!currentUser) {
      // This is the case when the account has been removed from its organization.
      alert(t('accountRemovedFromOrg'));

      auth.signOut();
      return null;
    }

    if (!currentOrganization || !profileOrganizations.length) {
      // This case is not ever expected to happen with our current schema.
      alert(t('noOrganizationForUser'));

      auth.signOut();
      return null;
    }

    return children(auth, currentUser, {
      currentOrganization,
      profileOrganizations,
    });
  } else {
    const redirectPath = `/signin?${queryString.stringify({
      [POST_SIGNIN_PATH_KEY]: `${pathname}${hash || ''}${window.location.search || ''}`,
    })}`;
    return <Navigate to={redirectPath} replace />;
  }
};

export const AuthProvider: React.FunctionComponent<{
  renderLoading: () => JSX.Element;
  children?: React.ReactNode;
}> = ({children, renderLoading}) => {
  const queryClient = useQueryClient();
  let firebaseUser = useFirebaseUser();
  const idToken = useFirebaseIdToken(firebaseUser);

  // The token query param set by the login app to allow
  // this app to sign in to Firebase.
  // Updating the query param state wasn't causing this component to re-render
  // so mirror it with our own state hook to ensure that we fetch
  // the user's profile, etc, after exchanging the custom token param for a JWT.
  const [urlToken, setUrlToken] = useQueryParam('token', StringParam);
  const [token, setToken] = React.useState(urlToken);
  // We want to avoid making any requests for the user's profile, etc, until after
  // we've exchanged the custom token param for a fresh JWT to prevent a race condition
  // when switching orgs using a signup link. Otherwise if a user had already authenticated
  // (with the old org) the backend's auth cache would return the wrong org for the user.
  // Using a new JWT clears the cache and prevents API errors.
  if (token) {
    firebaseUser = null;
  }

  // Set once any request meets the network_restricted hold (lib/api/api.ts): the API refuses the
  // org this session is in from here, so the page that says why replaces the app rather than
  // leaving it on a spinner.
  const [isNetworkRestricted, setIsNetworkRestricted] = React.useState(false);
  // Read by the listener below, which is added once.
  const currentOrganizationIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const onNetworkRestricted = (event: Event) => {
      // A refusal for some other org — a stale link, say — leaves this one usable.
      const {organizationId} = (event as CustomEvent<NetworkRestrictedDetail>).detail ?? {};
      if (!organizationId || organizationId === currentOrganizationIdRef.current) {
        setIsNetworkRestricted(true);
      }
    };
    window.addEventListener(NETWORK_RESTRICTED_EVENT, onNetworkRestricted);
    return () => window.removeEventListener(NETWORK_RESTRICTED_EVENT, onNetworkRestricted);
  }, []);

  const {data: currentUser} = useUserProfileQuery(firebaseUser);
  const {data: profileOrganizations} = useOrganizationQuery(currentUser);

  const [currentOrganization, setCurrentOrganization] = useStateWithDeps(
    profileOrganizations?.[0] ?? null,
    [profileOrganizations?.[0]?.id]
  );

  React.useEffect(() => {
    if (currentOrganization) {
      setContextOrganizationId(currentOrganization.id);
    }
    currentOrganizationIdRef.current = currentOrganization?.id ?? null;
  }, [currentOrganization]);

  const [auth, setAuth] = React.useState<Auth | undefined>(undefined);

  // This useEffect ensures a continuity of rendering pages. When the app is
  // first running and we don’t know if the user is logged in or not, auth is
  // undefined and we render a loading indicator. This loading indicator
  // continues while the profile and organization are loading (if the user is
  // actually logged in).
  //
  // If the user is logged out and logs in, the state says as a "logged out"
  // auth object until the profile and organization have completely loaded
  // (either with values or null responses). This keeps us from unmounting all
  // components, which would happen if we switched to a top level loading
  // indicator.

  const [isLoggingOut, setLoggingOut] = React.useState(false);

  React.useEffect(() => {
    async function signIn(signInToken: string) {
      try {
        const auth = getAuth();
        await signInWithCustomToken(auth, signInToken);
        // Force getting a new token to prevent a race when switching
        // orgs via signup links.
        await auth?.currentUser?.getIdToken(true);
        setToken(null);
        // Remove token from URL
        setUrlToken(undefined);
      } catch (e) {
        // If we encounter any errors, redirect back to the login app to sign in again.
        console.error(e);
        const url = new URL(window.location.href);
        url.searchParams.delete('token');
        redirectToLoginApp(`${LOGIN_APP_URL}?redirect=${url.href}`);
      }
    }

    const route = window.location.pathname.split('/')[1];
    if (firebaseUser === null) {
      // If we're currently unauthenticated and were just passed a token
      // (from signing in via the login app and getting redirected back to HF), sign in.
      if (token) {
        signIn(token);
      }
      // Don't redirect to the login app if we are on the signup pages
      else if (route === 'signup' || route === 'signupSuccess') {
        setAuth(makeLoggedOutAuth());
      }
      // Don't redirect to the login app if we are in the process of logging out of HF.
      // Redirect for that scenario is handled in the signOut function — redirectToLoginApp
      // also stands down for the sign-outs this state flag can't see, like a revoked token.
      else if (!isLoggingOut) {
        redirectToLoginApp(`${LOGIN_APP_URL}?redirect=${window.location.href}`);
      }
    } else if (
      firebaseUser &&
      idToken &&
      currentUser &&
      currentOrganization &&
      profileOrganizations?.length
    ) {
      setAuth(
        makeLoggedInAuth(
          queryClient,
          firebaseUser,
          idToken,
          currentUser,
          currentOrganization,
          profileOrganizations,
          setCurrentOrganization,
          setLoggingOut
        )
      );

      // If we were logged in and then the situation changes, we clear auth. If
      // we’re still logged in (and this ran because the profile or organization
      // objects changed) then we’re going to immediately re-setAuth with
      // another logged in state. If we logged out (meaning firebaseUser is
      // null) we’ll immediately setAuth with a logged-out state.
      //
      // The other case is if we switch auth from one user to another without an
      // intermediate log out, in which case we don’t want to render logged in
      // until the profile and organization change, but we don’t want to render
      // logged out because that might cause redirects. Instead, we’ll render
      // the loading indicator (auth === undefined) until organization and
      // profile load, in which case we’ll setAuth back to the logged in state.
      return () => {
        setAuth(undefined);
      };
    }
  }, [
    queryClient,
    firebaseUser,
    idToken,
    currentUser,
    currentOrganization,
    profileOrganizations,
    setCurrentOrganization,
    token,
    setToken,
    setUrlToken,
    isLoggingOut,
  ]);

  if (isNetworkRestricted) {
    // A direct membership the profile didn't flag, other than the org that refused. Not the
    // refusing org's children: they fall under its policy too.
    const reachesAnotherOrganization = !!currentUser?.organizations.some(
      (org) => !org.networkRestricted && org.id !== currentOrganization?.id
    );
    return (
      <NetworkRestricted
        onSwitchOrganization={
          reachesAnotherOrganization ? () => setWindowLocationHref('/organizations') : undefined
        }
        onSignOut={async () => {
          queryClient.clear();
          setLoggingOut(true);
          startSignOutHandoff();
          await getAuth().signOut();
          setWindowLocationHref(`${LOGIN_APP_URL}/signOut`);
        }}
      />
    );
  } else if (auth === undefined) {
    return renderLoading();
  } else {
    return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
  }
};
